import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** @type {WeakMap<CartItemsComponent, ReturnType<typeof setTimeout>>} */
const cartRefreshTimers = new WeakMap();

/**
 * Cart mutations run one at a time. Overlapping /cart/change calls make the
 * later ones reference line items the earlier ones already removed, which the
 * Cart API rejects with "no valid id or line parameter".
 *
 * @type {Promise<void>}
 */
let cartMutationQueue = Promise.resolve();

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('cart:refresh', this.#handleCartRefresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('cart:refresh', this.#handleCartRefresh);
  }

  #isLandingCartHost() {
    return Boolean(this.closest('[data-cart-drawer-host]'));
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.#removeLines([{ line, key: this.#keyForLine(line) }]);
    }

    this.updateQuantity({
      line,
      key: this.#keyForLine(line),
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal. A grouped bundle card covers several cart
   * lines and passes all of their keys.
   * @param {string | number} keys - Space separated cart line item keys.
   */
  onLineItemRemove(keys) {
    const targets = String(keys)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((key) => ({ key }));

    this.#removeLines(targets);
  }

  /**
   * Clears the given cart lines and takes their row off screen straight away.
   * @param {Array<{line?: number, key?: string}>} targets - The lines to clear.
   */
  #removeLines(targets) {
    const [first] = targets;

    if (!first) return;

    const keys = targets.map(({ key }) => key).filter((key) => typeof key === 'string');

    if (keys.length > 1 && keys.length === targets.length) {
      cartMutationQueue = cartMutationQueue.then(() => this.#requestBulkRemoval(keys)).catch(() => {});
    } else {
      for (const target of targets) {
        this.updateQuantity({ ...target, quantity: 0, action: 'clear' });
      }
    }

    const cartItemRowToRemove = first.key ? this.#rowForKey(first.key) : this.#rowForLine(first.line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {string} [config.key] - The line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    // Read the key before the row is removed optimistically.
    const key = config.key ?? this.#keyForLine(config.line);

    cartMutationQueue = cartMutationQueue.then(() => this.#requestQuantityUpdate(config, key)).catch(() => {});

    return cartMutationQueue;
  }

  /**
   * A row covers every line of a grouped bundle, so line indexes no longer line
   * up with the position of the row in the table.
   * @param {number} [line] - The 1 based line index.
   * @returns {HTMLElement | null} The row rendering that line.
   */
  #rowForLine(line) {
    if (!line) return null;

    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-lines~="${line}"]`));
  }

  /**
   * @param {string} [key] - A cart line item key.
   * @returns {HTMLElement | null} The row rendering that line.
   */
  #rowForKey(key) {
    if (!key) return null;

    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-keys~="${key}"]`));
  }

  /**
   * @param {number} [line] - The 1 based line index.
   * @returns {HTMLElement | null} The quantity input driving that line.
   */
  #quantityInputForLine(line) {
    if (!line) return null;

    const selector = `input[data-cart-line="${line}"]`;

    return /** @type {HTMLElement | null} */ (this.querySelector(selector) ?? document.querySelector(selector));
  }

  /**
   * @param {number} [line] - The 1 based line index.
   * @returns {string | undefined} The stable key for that line.
   */
  #keyForLine(line) {
    return this.#quantityInputForLine(line)?.dataset.cartLineKey;
  }

  /**
   * Sends a single cart change and brings the rendered cart back in sync.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [key] - The line item key.
   */
  async #requestQuantityUpdate(config, key) {
    const { line, quantity } = config;

    // Without either identifier the Cart API rejects the call, and the row was
    // already taken off screen optimistically.
    if (!key && !line) {
      await this.#renderCartSection();
      return;
    }

    const body = JSON.stringify({
      // Line indexes shift whenever another row is removed, and apps that
      // translate them work from their own cached cart. The key is stable.
      ...(key ? { id: key } : { line }),
      quantity: quantity,
      sections: this.#sectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    await this.#submitCartMutation(Theme.routes.cart_change_url, body, config.action, line);
  }

  /**
   * Clears several lines in one request. Removing a line rekeys the ones left
   * behind, so a follow up call carrying a key read before that point comes
   * back as "no valid id or line parameter".
   * @param {string[]} keys - The line item keys to clear.
   */
  async #requestBulkRemoval(keys) {
    const body = JSON.stringify({
      updates: Object.fromEntries(keys.map((key) => [key, 0])),
      sections: this.#sectionsToUpdate(),
      sections_url: window.location.pathname,
    });

    await this.#submitCartMutation(Theme.routes.cart_update_url, body, 'clear', undefined);
  }

  /**
   * @returns {string} Every cart section currently on the page.
   */
  #sectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate).join(',');
  }

  /**
   * Posts a cart mutation and brings the rendered cart back in sync.
   * @param {string} url - The cart endpoint.
   * @param {string} body - The serialised request body.
   * @param {string} action - The action, used for the performance marker.
   * @param {number | undefined} line - The line the mutation targets, if any.
   */
  async #submitCartMutation(url, body, action, line) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    cartTotal?.shimmer();

    try {
      const response = await fetch(url, fetchConfig('json', { body }));
      const parsedResponseText = JSON.parse(await response.text());

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        await this.#renderCartSection();
        return;
      }

      const sections = parsedResponseText.sections ?? {};
      const sectionHTML = sections[this.sectionId];

      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: this.#readItemCount(sectionHTML) ?? parsedResponseText.item_count ?? 0,
          source: 'cart-items-component',
          sections,
        })
      );

      await this.#renderCartSection(sectionHTML);

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      // Rows are removed optimistically, so anything that goes wrong has to be
      // reconciled or the drawer keeps showing a cart that no longer exists.
      await this.#renderCartSection();
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Renders the cart section, falling back to a live render whenever the cart
   * response came back without usable section markup.
   * @param {string} [html] - Section markup from the cart response.
   */
  async #renderCartSection(html) {
    if (html) {
      try {
        await morphSection(this.sectionId, html);
        return;
      } catch (error) {
        console.error(error);
      }
    }

    await sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
  }

  /**
   * @param {string} [html] - Section markup from the cart response.
   * @returns {number | undefined} The item count the markup renders.
   */
  #readItemCount(html) {
    if (!html) return undefined;

    const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;

    return count ? parseInt(count, 10) : undefined;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number | undefined} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = /** @type {HTMLInputElement | null} */ (this.#quantityInputForLine(line));

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    // The per-line error markup is optional, and throwing here would skip the
    // reconcile that puts the cart back on screen.
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.error(parsedResponseText.errors);
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (this.#isLandingCartHost()) {
        this.#renderHostCartSection().catch(() => {});
        return;
      }
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    if (this.#isLandingCartHost()) {
      const html = event.detail.data.sections?.[this.sectionId];
      if (html) {
        morphSection(this.sectionId, html);
        this.#updateCartQuantitySelectorButtonStates();
      }
      this.#renderHostCartSection().catch(() => {});
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Debounces external cart refreshes per rendered cart. Drawer and cart-page
   * components can coexist, so one must not cancel the other's refresh.
   * @param {Event} event
   */
  #handleCartRefresh = (event) => {
    const reason =
      event instanceof CustomEvent && event.detail?.reason === 'kite' ? 'kite' : 'external-cart-mutation';

    if (this.#isLandingCartHost()) {
      this.#scheduleExternalCartRefresh(() => this.#renderHostCartSection(), reason);
      return;
    }

    if (this.closest('cart-drawer-component.tn-cart-drawer')) {
      this.#scheduleExternalCartRefresh(() => this.#renderStandardCartSection(), reason);
      return;
    }

    this.#scheduleExternalCartRefresh(() => this.#renderCartPageSection(), reason);
  };

  /**
   * @param {() => Promise<unknown>} refresh
   * @param {'kite' | 'external-cart-mutation'} reason
   */
  #scheduleExternalCartRefresh(refresh, reason) {
    const pendingTimer = cartRefreshTimers.get(this);
    if (pendingTimer) clearTimeout(pendingTimer);

    cartRefreshTimers.set(
      this,
      setTimeout(() => {
        cartRefreshTimers.delete(this);
        Promise.resolve()
          .then(refresh)
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:refresh:complete', { detail: { reason } }));
          })
          .catch(() => {});
      }, 0)
    );
  }

  #renderStandardCartSection() {
    const sectionId = this.sectionId;
    const drawer = this.closest('cart-drawer-component.tn-cart-drawer');

    return sectionRenderer.getSectionHTML(sectionId, false, new URL(window.location.href)).then((html) => {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = documentFragment.querySelector('cart-drawer-component.tn-cart-drawer');

      if (!drawer || !nextDrawer) throw new Error('Cart drawer missing from section response');

      // Only morph the drawer. Re-rendering the entire header causes a
      // visible page/header reset and can close the drawer while an external
      // cart integration is changing reward lines.
      morph(drawer, nextDrawer);
      this.#updateCartQuantitySelectorButtonStates();
    });
  }

  #renderCartPageSection() {
    return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
      this.#updateCartQuantitySelectorButtonStates();
    });
  }

  #renderHostCartSection() {
    const sectionId = this.sectionId;
    const drawer = document.querySelector('[data-cart-drawer-host] cart-drawer-component');
    const wasOpen = Boolean(drawer?.querySelector('dialog[open]'));
    const sectionUrl = new URL(window.location.pathname, window.location.origin);

    return sectionRenderer
      .renderSection(sectionId, { cache: false, url: sectionUrl })
      .then(() => {
        this.#updateCartQuantitySelectorButtonStates();
        if (wasOpen) this.#reopenCartDrawer('[data-cart-drawer-host] cart-drawer-component');
      })
      .catch(() => {
        return sectionRenderer
          .renderSection(sectionId, { cache: false })
          .then(() => {
            this.#updateCartQuantitySelectorButtonStates();
            if (wasOpen) this.#reopenCartDrawer('[data-cart-drawer-host] cart-drawer-component');
          });
      });
  }

  /**
   * @param {string} selector
   * @param {number} [attempt]
   */
  #reopenCartDrawer(selector, attempt = 0) {
    const nextDrawer = document.querySelector(selector);

    if (nextDrawer && 'open' in nextDrawer && typeof nextDrawer.open === 'function') {
      /** @type {{ open: () => void }} */ (nextDrawer).open();

      requestAnimationFrame(() => {
        const dialog = nextDrawer.querySelector('dialog');
        if (!dialog?.open && attempt < 10) {
          setTimeout(() => this.#reopenCartDrawer(selector, attempt + 1), 100);
        }
      });
      return;
    }

    if (attempt < 10) {
      setTimeout(() => this.#reopenCartDrawer(selector, attempt + 1), 100);
    }
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
